import { AnalyticsBrowser } from '@june-so/analytics-next';
import { ACCOUNT_EVENTS } from './events';
import mixpanel from 'mixpanel-browser';

/**
 * AnalyticsHelper class to initialize and track user analytics
 * @class AnalyticsHelper
 */
export class AnalyticsHelper {
  /**
   * @constructor
   * @param {Object} [options={}] - options for analytics
   * @param {string} [options.token] - analytics token
   */
  constructor({ token: analyticsToken } = {}) {
    this.analyticsToken = analyticsToken;
    this.analytics = null;
    this.user = {};
    this.mixpanel = null;
  }

  /**
   * Initialize analytics
   * @function
   * @async
   */
  async init() {
    if (this.analyticsToken) {
      try {
        const [analytics] = await AnalyticsBrowser.load({
          writeKey: this.analyticsToken,
        });
        this.analytics = analytics;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize June.so Analytics:', error);
      }
    }

    const mixpanelToken = window.mixpanelConfig?.token;
    if (mixpanelToken) {
      try {
        mixpanel.init(mixpanelToken);
        this.mixpanel = mixpanel;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize Mixpanel:', error);
      }
    }
  }

  /**
   * Identify the user
   * @function
   * @param {Object} user - User object
   */
  identify(user) {
    if (!user) {
      return;
    }

    this.user = user;

    if (this.analytics) {
      this.analytics.identify(this.user.email, {
        userId: this.user.id,
        email: this.user.email,
        name: this.user.name,
        avatar: this.user.avatar_url,
      });

      const { accounts, account_id: accountId } = this.user;
      const [currentAccount] = accounts.filter(
        account => account.id === accountId
      );
      if (currentAccount) {
        this.analytics.group(currentAccount.id, this.user.id, {
          name: currentAccount.name,
        });
      }
    }

    if (this.mixpanel) {
      this.mixpanel.identify(this.user.id);
    }
  }

  /**
   * Track any event
   * @function
   * @param {string} eventName - event name
   * @param {Object} [properties={}] - event properties
   */
  track(eventName, properties = {}) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        eventProperties: properties,
      });
    }

    if (this.analytics) {
      this.analytics.track({
        userId: this.user.id,
        event: eventName,
        properties,
      });
    }

    if (this.mixpanel) {
      const mixpanelProperties = {
        ...properties,
        distinct_id: this.user.id,
      };
      const mixpanelEventName = this.toSnakeCase(eventName);
      this.mixpanel.track(mixpanelEventName, mixpanelProperties);
    }
  }

  /**
   * Track the page views
   * @function
   * @param {Object} params - Page view properties
   */
  page(params) {
    if (!this.analytics) {
      return;
    }

    this.analytics.page(params);
  }

  trackLogin(properties) {
    const { accounts, accountId, userId, userName, userEmail } = properties;

    const [currentAccount] = accounts.filter(
      account => account.id === accountId
    );

    const loginProperties = {
      accountId,
      accountName: currentAccount.name,
      userId,
      userName,
      userEmail,
    };

    this.track(ACCOUNT_EVENTS.LOGIN, loginProperties);
  }

  /**
   * Converts event name to snake_case (lowercase with underscores)
   * @private
   * @param {string} eventName - Event name
   * @returns {string} Event name in snake_case
   */
  // eslint-disable-next-line class-methods-use-this
  toSnakeCase(eventName) {
    return eventName
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .toLowerCase(); // Convert to lowercase
  }
}

// This object is shared across, the init is called in app/javascript/packs/application.js
export default new AnalyticsHelper(window.analyticsConfig);
